import { useCallback, useEffect, useState } from 'react';

import { API_BOT_IDENTITY } from '../../common/EsConstants';
import { esDelete, esPost, esRead } from '../../common/EsFetch';
import { useEsChatBotFlag } from '../../common/EsFlags';
import { useEsClipboard } from '../../common/EsClipboard';
import { esClock } from '../../common/EsDate';
import { useEsStrings } from '../../common/EsStrings';

// The platforms an account links to, each a row of its own; Discord's keeps the bare testids it had first.
const PROVIDERS = [ { provider: 'DISCORD', row: 'discord', suffix: '' }, { provider: 'SLACK', row: 'slack', suffix: '-slack' } ];

/**
 * The account page's Discord and Slack block (es-chat-bot.md, Bridges), while rolloutEsChatBot
 * is on: per platform, a code to run as /escapade me there, so the bot knows the member as this
 * account at the tables they sit at; and every account linked on either, each with an unlink.
 * One code is pending at a time, drawn in its platform's row. The account page's own form
 * classes, as the rest of the Personal panel.
 */
export default function EsAccountBridge() {
    const t = useEsStrings();
    const botOn = useEsChatBotFlag();
    const { copied, copy } = useEsClipboard();
    const [ mine, setMine ] = useState( null );
    const [ busy, setBusy ] = useState( false );
    const [ error, setError ] = useState( null );

    const load = useCallback( () => esRead( API_BOT_IDENTITY )
        .then( ( pMine ) => setMine( pMine ?? null ) )
        .catch( () => setMine( null ) ), [] );

    useEffect( () => {
        if ( botOn ) load();
    }, [ botOn, load ] );

    const act = async ( pWrite ) => {
        setBusy( true );
        setError( null );
        try {
            await pWrite();
            await load();
        }
        catch ( aFailure ) {
            setError( aFailure?.message ?? t( 'bot-bridge-error', 'That did not work. Try again.' ) );
        }
        finally {
            setBusy( false );
        }
    };

    // Only an answer shaped as one: anything else is esback saying no, or nobody's answer at all.
    if ( !botOn || !Array.isArray( mine?.identities ) ) return null;

    const aCode = mine.code?.code ?? null;
    const aCodeFor = aCode ? ( mine.code.provider ?? 'DISCORD' ) : null;
    const providerName = ( pProvider ) => pProvider === 'SLACK'
        ? t( 'bot-bridge-provider-slack', 'Slack' )
        : t( 'bot-bridge-provider-discord', 'Discord' );

    return (
        <div className="mt-8" data-testid="es-bridge-account">
            <h4 className="mb-2 text-lg font-bold text-heading" data-testid="es-bridge-account-heading">
                { t( 'bot-bridge-account-heading', 'Discord and Slack' ) }
            </h4>
            <p className="mb-4 text-sm text-body" data-testid="es-bridge-account-blurb">
                { t( 'bot-bridge-account-blurb',
                    'Make a code, then run /imp me with it in any channel the bot is in.' ) }
            </p>

            { PROVIDERS.map( ( { provider, row, suffix } ) =>
                <div key={ provider } className="mb-4" data-testid={ 'es-bridge-me-row-' + row }>
                    <p className="mb-2 text-sm font-bold text-body" data-testid={ 'es-bridge-me-name-' + row }>
                        { providerName( provider ) }
                    </p>

                    { aCodeFor === provider &&
                        <p className="mb-4 flex items-center gap-2 text-sm text-body" data-testid="es-bridge-me-code-row">
                            <code data-testid="es-bridge-me-code">{ aCode }</code>
                            <button type="button"
                                    data-testid="es-bridge-me-copy"
                                    aria-label={ t( 'bot-bridge-copy', 'Copy the command' ) }
                                    data-tip={ t( 'bot-bridge-copy', 'Copy the command' ) }
                                    onClick={ () => copy( aCode ) }>
                                <i className="pi pi-copy" aria-hidden="true" />
                            </button>
                            { copied &&
                                <span data-testid="es-bridge-me-copied" aria-live="polite">{ t( 'bot-bridge-copied', 'Copied' ) }</span> }
                            <span className="text-aside" data-testid="es-bridge-me-expires">
                                { t( 'bot-bridge-code-expires', 'Good until {time}', { time: esClock( mine.code.expires ) } ) }
                            </span>
                        </p> }

                    <button type="button"
                            data-testid={ 'es-bridge-me-mint' + suffix }
                            disabled={ busy }
                            className="buttonSubmit flex w-full items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={ () => act( () => esPost( API_BOT_IDENTITY + ( provider === 'DISCORD' ? '' : '?provider=' + provider ) ) ) }>
                        { aCodeFor === provider
                            ? t( 'bot-bridge-mint-again', 'Make a new code' )
                            : t( 'bot-bridge-account-mint', 'Make a code' ) }
                    </button>
                </div> ) }

            { mine.identities.length === 0
                ? <p className="mt-4 text-sm text-aside" data-testid="es-bridge-account-none">
                      { t( 'bot-bridge-account-none', 'No Discord or Slack account is linked.' ) }
                  </p>
                : <ul className="mt-4 flex flex-col gap-2 text-sm text-body" data-testid="es-bridge-identities">
                      { mine.identities.map( ( pOne ) =>
                          <li key={ pOne.id } className="flex items-center justify-between gap-2" data-testid={ 'es-bridge-identity-' + pOne.id }>
                              <span>
                                  <span className="text-aside" data-testid={ 'es-bridge-identity-provider-' + pOne.id }>
                                      { providerName( pOne.provider ) }
                                  </span>
                                  { ' ' }
                                  { t( 'bot-bridge-account-linked', 'Linked as {name}', { name: pOne.externalName ?? '?' } ) }
                              </span>
                              <button type="button"
                                      data-testid={ 'es-bridge-identity-unlink-' + pOne.id }
                                      disabled={ busy }
                                      className="text-danger underline disabled:opacity-50"
                                      onClick={ () => act( () => esDelete( API_BOT_IDENTITY + '/' + pOne.id ) ) }>
                                  { t( 'bot-bridge-unlink', 'Unlink' ) }
                              </button>
                          </li> ) }
                  </ul> }

            { error &&
                <p className="formError" role="alert" data-testid="es-bridge-account-error">{ error }</p> }
        </div>
    );
}
